// Print tokens for the activation pack documents.
//
// Mirrors the subset of HG-CC's `scripts/collateral/lib/brand.mjs` that the
// terminal builder (`scripts/collateral/build-activation-pack.mjs`) uses. Kept
// as its own file, and as literal CSS rather than Tailwind classes, because the
// pack renders inside an isolated iframe with no app stylesheet: nothing in
// `tailwind.config.ts` reaches it, by design.
//
// REVISED 2026-08-09 — the material carries the CUSTOMER's brand. It used to
// carry Help Genie's, which put our mark and our navy on a poster the customer
// sticks to their own front door. Help Genie now signs the foot of the sheet
// instead. See `printBrand.ts` for the reasoning and the colour derivation.
//
// The palette below is roles, not colours, and each role resolves to a CSS
// custom property that `docCss` defines once per document from the resolved
// brand. That is why a page builder can name `palette.primary` without knowing
// whose primary it is, and why adding a brand-aware page needs no plumbing.

import type { PrintBrand } from './printBrand';

/**
 * Print roles. Every value is a custom property with a Help Genie fallback, so
 * a sheet rendered outside `docCss` — a test, a snapshot — still has colour.
 */
export const palette = {
  /** Headline colour on light sheets, ground of the primary-toned sheets. */
  primary: 'var(--pk-primary, #4E9CFF)',
  /** Accent rules and the secondary-toned sheets. */
  secondary: 'var(--pk-secondary, #FF4DB8)',
  /** The call-to-action line on dark sheets. */
  highlight: 'var(--pk-highlight, #FFBB33)',

  /** Headings on white. */
  ink: 'var(--pk-ink, #28406F)',
  /** The dark ground of covers and hero posters. */
  inkDeep: 'var(--pk-ink-deep, #1A2C4F)',

  /* Neutrals. Deliberately NOT brand-derived — body text and hairlines are
     legibility furniture, and tinting them is how a printed page starts looking
     like a themed website instead of a document. */
  bodyFg: '#222B45',
  mutedFg: '#6B7280',
  border: '#E5EAF0',
  borderLight: '#EEF1F6',

  pageTint: 'var(--pk-tint, #F9FAFC)',
  rowAlt: 'var(--pk-row-alt, #F4F6FB)',
} as const;

export const font = {
  googleFontsLink:
    'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&family=Baloo+Paaji+2:wght@500;600;700;800&display=swap',
  sans: `'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
  display: `'Baloo Paaji 2', 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif`,
  mono: `'JetBrains Mono', Menlo, Consolas, monospace`,
} as const;

/** A4 portrait, in millimetres. Every page in every document is this size. */
export const A4 = { w: 210, h: 297 } as const;

export const escapeHtml = (value: string): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * The Help Genie mark, served from `public/`. Absolute so it resolves the same
 * in the preview iframe, in the print window and in a saved PDF.
 *
 * The one deliberate divergence from the Operations copy of this file, which
 * serves the same artwork as "Help Genie Logo IOS.png". Same image, renamed
 * here: a filename with spaces in a hot URL is a footgun.
 */
export const markUrl = (): string => `${window.location.origin}/help-genie-mark.png`;

/**
 * The customer's lockup: their logo when they have one, their name set in
 * display type when they do not.
 *
 * Never both. A logo almost always contains the name already, and a wordmark
 * printed under its own wordmark is the tell of software that does not know
 * what it is looking at.
 *
 * The logo is `object-fit: contain` inside a box of the given height and three
 * times the width, so a wide wordmark and a square avatar both land at the same
 * optical weight without either being stretched.
 */
export const brandLockup = (
  brand: PrintBrand,
  heightMm: number,
  tone: 'dark' | 'light' = 'dark',
): string => {
  if (brand.logoUrl) {
    return `<div class="lockup ${tone}" style="height:${heightMm}mm">
      <img class="brandlogo" src="${escapeHtml(brand.logoUrl)}" alt="${escapeHtml(brand.name)}"
        style="height:${heightMm}mm;max-width:${heightMm * 3.4}mm" />
    </div>`;
  }
  return `<div class="lockup ${tone}" style="height:${heightMm}mm">
    <span class="brandword" style="font-size:${heightMm * 0.72}mm">${escapeHtml(brand.name)}</span>
  </div>`;
};

/**
 * The customer's mark alone, for the pieces too small to carry a lockup — the
 * pocket card and the counter strip, where the name is already set in type
 * directly beneath it.
 *
 * Returns nothing when there is no logo, rather than substituting Help Genie's.
 * An empty corner on a business card is unremarkable; somebody else's mark on
 * it is the whole bug this file was rewritten to fix.
 */
export const brandMark = (brand: PrintBrand, heightMm: number): string =>
  brand.logoUrl
    ? `<img class="brandmark" src="${escapeHtml(brand.logoUrl)}" alt=""
        style="height:${heightMm}mm;max-width:${heightMm * 3.4}mm" />`
    : '';

/**
 * Help Genie's signature at the foot of a sheet. Small, set in the sheet's own
 * muted tone, and never larger than the customer's own name above it.
 */
export const poweredBy = (tone: 'dark' | 'light' = 'light'): string =>
  `<span class="poweredby ${tone}">
    <i class="hgmark"></i><span>Powered by Help Genie</span>
  </span>`;

/**
 * Document-wide CSS. Defines the brand's custom properties once at the root,
 * which is what makes every `palette.*` reference above resolve to this
 * customer's colours for the whole document.
 */
export const docCss = (brand: PrintBrand): string => `
  @import url('${font.googleFontsLink}');
  @page { size: ${A4.w}mm ${A4.h}mm; margin: 0; }
  :root {
    --pk-primary: ${brand.primary};
    --pk-secondary: ${brand.secondary};
    --pk-highlight: ${brand.highlight};
    --pk-ink: ${brand.ink};
    --pk-ink-deep: ${brand.inkDeep};
    --pk-tint: ${brand.tint};
    --pk-row-alt: ${brand.rowAlt};
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #FFFFFF; }
  body {
    font-family: ${font.sans};
    color: ${palette.bodyFg};
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .sheet {
    width: ${A4.w}mm; height: ${A4.h}mm;
    position: relative; overflow: hidden;
    page-break-after: always; break-after: page; background: #FFFFFF;
  }
  .sheet:last-child { page-break-after: auto; break-after: auto; }
  svg { display: block; }

  /* Cut and fold guides. Hairline, prints faintly, sits on the trim path. */
  .cut  { position: absolute; inset: 0; border: 0.2mm dashed rgba(0,0,0,0.3); pointer-events: none; }
  .cut.oncolour { border-color: rgba(255,255,255,0.4); }

  /* Small page footer so a loose sheet can be put back in order. */
  .pagenum {
    position: absolute; right: 9mm; bottom: 5mm;
    font-size: 2.7mm; color: #AAB2C0; letter-spacing: 0.06em;
  }
  .pagenum.oncolour { color: rgba(255,255,255,0.45); }
  .sheetnote {
    position: absolute; left: 9mm; bottom: 5mm;
    font-size: 2.7mm; color: #98A1AF; letter-spacing: 0.03em; max-width: 165mm;
  }
  .sheetnote.oncolour { color: rgba(255,255,255,0.5); }

  /* On screen the sheets stack with a gap so the preview reads as a document.
     Print collapses it — @media print wins over the base rule. */
  @media screen {
    body { background: #EEF1F6; padding: 6mm 0; }
    .sheet { margin: 0 auto 6mm; box-shadow: 0 2mm 6mm rgba(26,44,79,0.14); }
  }
  @media print {
    body { background: #FFFFFF; padding: 0; }
    .sheet { margin: 0; box-shadow: none; }
  }
`;

/** The lockup and signature rules. Paired with `brandLockup` and `poweredBy`. */
export const brandCss = (): string => `
  .lockup { display: flex; align-items: center; gap: 0.28em; }
  .lockup img.brandlogo { display: block; width: auto; object-fit: contain; object-position: left center; }
  .lockup .brandword {
    font-family: ${font.display}; font-weight: 700;
    line-height: 1; letter-spacing: -0.01em;
  }
  .lockup.dark .brandword { color: #FFFFFF; }
  .lockup.light .brandword { color: ${palette.ink}; }
  img.brandmark { display: block; width: auto; object-fit: contain; object-position: left center; }

  .poweredby { display: inline-flex; align-items: center; gap: 1.4mm;
    font-size: 2.9mm; letter-spacing: 0.08em; text-transform: uppercase; }
  .poweredby .hgmark {
    display: block; flex: 0 0 auto; width: 3.6mm; height: 3.6mm;
    background: url('${markUrl()}') no-repeat center / contain;
  }
  .poweredby.light { color: #98A1AF; }
  .poweredby.dark { color: rgba(255,255,255,0.55); }
`;

/**
 * The colourways a sheet can be set in, as brand roles.
 *
 * RENAMED 2026-08-09 from `blue` / `magenta` / `amber`. The keys used to name
 * Help Genie's own colours, which stopped being true the moment the ground
 * became the customer's primary — a tone called "blue" printing orange is
 * exactly the kind of quiet lie that let Help Genie branding sit on customer
 * material for as long as it did.
 */
export const TONES = {
  dark: { bg: palette.inkDeep, fg: '#FFFFFF', accent: palette.secondary, cta: palette.highlight, mark: 'dark' },
  primary: { bg: palette.primary, fg: '#FFFFFF', accent: palette.inkDeep, cta: '#FFFFFF', mark: 'dark' },
  secondary: { bg: palette.secondary, fg: '#FFFFFF', accent: palette.inkDeep, cta: '#FFFFFF', mark: 'dark' },
  highlight: { bg: palette.highlight, fg: palette.inkDeep, accent: palette.inkDeep, cta: palette.inkDeep, mark: 'light' },
  light: { bg: '#FFFFFF', fg: palette.ink, accent: palette.primary, cta: palette.secondary, mark: 'light' },
} as const;

/** Tones that print white type on a colour, so page furniture has to invert. */
export const isOnColour = (tone: keyof typeof TONES): boolean =>
  tone !== 'light' && tone !== 'highlight';

export type PackTone = keyof typeof TONES;
